import { createFileRoute, Link } from "@tanstack/react-router";
import {
  CheckCircle2,
  Database,
  Globe,
  LineChart,
  Shield,
  Users,
  Zap,
} from "lucide-react";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { tools } from "@/tools";

/**
 * Bump when the About copy is materially revised. Rendered as "Last reviewed"
 * and used as dateModified — a static literal on purpose: a build-time date
 * would claim the page changed on every deploy.
 */
const LAST_REVIEWED = "2026-08-26";

export const Route = createFileRoute("/about")({
  head: () => {
    const locale = getLocale();
    const url = localeUrl("/about");
    return {
      meta: [
        { title: "About Toolverse — Who Builds It and How We Check Our Work" },
        {
          name: "description",
          content: `Toolverse is a free collection of ${tools.length} browser tools built by the Toolverse Team. Learn who we are, what we build, and how every calculator is verified.`,
        },
        {
          property: "og:title",
          content: "About Toolverse — Who Builds It and How We Check Our Work",
        },
        {
          property: "og:description",
          content: `A free collection of ${tools.length} precision-built browser tools. Who we are, what we build, and how we verify every formula.`,
        },
        ...i18nOgMeta("/about"),
      ],
      links: [...i18nLinks("/about")],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": url,
            name: "About Toolverse",
            url,
            inLanguage: locale,
            dateModified: LAST_REVIEWED,
            description: `Toolverse is a free collection of ${tools.length} browser-based tools built and verified by the Toolverse Team.`,
            // The author entity referenced by every blog post's byline.
            mainEntity: {
              "@type": "Organization",
              "@id": "https://toolverse.me/about#team",
              name: "Toolverse Team",
              url: "https://toolverse.me/about",
              parentOrganization: {
                "@type": "Organization",
                "@id": "https://toolverse.me/#organization",
                name: "Toolverse",
              },
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://toolverse.me/#organization",
              name: "Toolverse",
            },
          }),
        },
      ],
    };
  },
  component: AboutPage,
});

function Section({
  icon: Icon,
  title,
  id,
  children,
}: {
  icon: typeof Zap;
  title: string;
  id?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10" id={id}>
      <h2 className="text-xl font-bold tracking-tight mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        {title}
      </h2>
      <div className="space-y-3 text-sm md:text-base text-muted-foreground leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function AboutPage() {
  const locale = getLocale();
  // Noon UTC so the calendar date can't shift in any server/client timezone.
  const reviewed = new Date(`${LAST_REVIEWED}T12:00:00Z`).toLocaleDateString(
    locale,
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    },
  );

  return (
    <div className="flex-1 bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-12 md:py-16">
        {/* Header */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Zap className="h-3 w-3 text-primary" />
            <span className="text-[11px] font-semibold text-primary tracking-wide uppercase">
              {m.about_badge()}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4">
            {m.about_page_title()}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            {m.about_intro({ count: String(tools.length) })}
          </p>
          <p className="mt-3 text-xs text-muted-foreground/80">
            <time dateTime={LAST_REVIEWED}>
              {m.about_last_reviewed({ date: reviewed })}
            </time>
          </p>
        </div>

        {/* What We Build */}
        <Section icon={LineChart} title={m.about_section_what_we_build()}>
          <p>{m.about_what_we_build_p1()}</p>
          <p>{m.about_what_we_build_p2()}</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>{m.about_cat_calculators()}</li>
            <li>{m.about_cat_converters()}</li>
            <li>{m.about_cat_text()}</li>
            <li>{m.about_cat_dev()}</li>
            <li>{m.about_cat_science()}</li>
            <li>{m.about_cat_f1()}</li>
          </ul>
          <p>{m.about_more_tools()}</p>
        </Section>

        {/* Who We Are — the author entity behind every byline */}
        <Section icon={Users} title={m.about_section_team()} id="team">
          <p>{m.about_team_p1()}</p>
          <p>{m.about_team_p2()}</p>
        </Section>

        {/* How We Check Our Work */}
        <Section icon={CheckCircle2} title={m.about_section_verify()}>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>{m.about_verify_li1()}</li>
            <li>{m.about_verify_li2()}</li>
            <li>{m.about_verify_li3()}</li>
            <li>{m.about_verify_li4()}</li>
          </ul>
          <p>{m.about_verify_p()}</p>
        </Section>

        {/* F1 Fantasy in depth */}
        <Section icon={Zap} title={m.about_section_f1()}>
          <p>{m.about_f1_intro()}</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>{m.about_apex_desc()}</li>
            <li>{m.about_budget_desc()}</li>
            <li>{m.about_stats_desc()}</li>
          </ul>
        </Section>

        <Section icon={Database} title={m.about_section_data_sources()}>
          <p>{m.about_data_sources_p1()}</p>
          <p>{m.about_data_sources_p2()}</p>
        </Section>

        <Section icon={Shield} title={m.about_section_scoring()}>
          <p>{m.about_scoring_intro()}</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>{m.about_scoring_dq()}</li>
            <li>{m.about_scoring_sprint()}</li>
            <li>{m.about_scoring_bonuses()}</li>
            <li>{m.about_scoring_constructor()}</li>
          </ul>
          <p>{m.about_ppm_explanation()}</p>
        </Section>

        <Section icon={Zap} title={m.about_section_optimizer()}>
          <p>{m.about_optimizer_p1()}</p>
          <ul className="list-disc list-inside space-y-2 pl-2">
            <li>{m.about_optimizer_li1()}</li>
            <li>{m.about_optimizer_li2()}</li>
            <li>{m.about_optimizer_li3()}</li>
            <li>{m.about_optimizer_li4()}</li>
          </ul>
          <p>{m.about_optimizer_p2()}</p>
        </Section>

        {/* Contact */}
        <Section icon={Globe} title={m.about_section_contact()}>
          <p>{m.about_contact_text({ email: "contact@toolverse.me" })}</p>
        </Section>

        {/* Back link */}
        <div className="pt-4 border-t border-border">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {m.about_back()}
          </Link>
        </div>
      </div>
    </div>
  );
}
